/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable camelcase */

import { useTranslation } from 'react-i18next';

import Tooltip from 'components/onClickTooltip';
import {
  calculateEstimatedTradingFee,
  getFormattedLiquidationPrice,
  getOrderMetricsLabel,
  priceToUse,
} from 'components/placeorder/helper';
import { MARGIN_TYPES } from 'constants/constants';
import { MARGIN_MODE } from 'constants/enums';
import { calculateLiquidationPrice } from 'helpers/calculator_formulas';
import {
  getOrderValue,
  round,
  round_by_contract_size,
  round_by_tick_size,
} from 'helpers/formulas';
import { generateAssetUnitLabel } from 'helpers/localized';
import {
  calculateSpotImpactPrice,
  spotQuoteNotional,
  spotUnderlyingNotional,
} from 'helpers/spotFormulas';
import {
  assetPrecision,
  convertExponentialToDecimal,
  cropAfterDecimals,
  // cropAfterDecimals,
  isFutures,
  isNan,
  isOptions,
  negativeBalanceCheck,
  pluralize,
  trackerSymbolText,
} from 'helpers/utils';
import { defaultToHiphen } from 'helpers/validations';
import useSelectedProductBalance from 'hooks/componentHooks/useSelectedProductBalance';
import i18n from 'i18n/config';
import { getLabelByContractType } from 'reducers/helpers/trade';
import {
  markPriceState,
  // orderbooksSelector,
  spotPriceState,
} from 'selectors/priceSelectors';
import Box from 'UIKit/Box';

// import { useAppSelector } from 'storeHooks';

const LocalizedValue = ({ localeKey, options }) => {
  const { t } = useTranslation(['trading']);
  return t(`trading:${localeKey}`, options);
};

const getEstimatedPremiumTobeReceived = ({
  price,
  contract_size,
  size,
  settling_asset,
  isBuyActionState,
}) => {
  const premium = round(price * contract_size * size, settling_asset.minimum_precision);
  return {
    label: (
      <LocalizedValue
        localeKey={
          isBuyActionState ? 'estimatedPremiumToBePaid' : 'estimatedPremiumToBeReceived'
        }
      />
    ),
    value: (
      <>
        {premium}{' '}
        <Box as="span" textColor="secondary">
          {settling_asset.symbol}
        </Box>
      </>
    ),
  };
};

const marginLabel = (
  <Tooltip
    title={<LocalizedValue localeKey="estMarginRequired" />}
    body={<LocalizedValue localeKey="estMarginRequiredInformation" />}
    parentDivStyle={{ marginBottom: '0' }}
  />
);

export const getConfirmationTable = ({
  selected_product,
  position,
  side,
  estimatedPortfolioMargin,
  orderPrice,
  orderSize,
  orderMargin,
  isBuyActionState,
  leverage,
  marginType,
  reduceOnly,
  orderBook,
  orderType,
  postOnly,
  price_band,
}) => {
  const markPrice = markPriceState();
  const spotPrice = spotPriceState();
  const contractType = selected_product.contract_type;
  const assetSymbol = (() => {
    switch (selected_product.contract_type) {
      case 'move_options':
      case 'call_options':
      case 'put_options':
      case 'turbo_put_options':
      case 'turbo_call_options':
      case 'options_combos':
        return selected_product.underlying_asset.symbol;
      case 'spot':
        return isBuyActionState
          ? selected_product.underlying_asset.symbol
          : selected_product.quoting_asset.symbol;
      default:
        return selected_product.settling_asset.symbol;
    }
  })();

  const priceSymbol =
    contractType === 'interest_rate_swaps' ? '%' : selected_product.quoting_asset.symbol;
  const productTickSize = selected_product.tick_size;
  // const contractVal = parseFloat(selected_product.contract_value, 10);
  const positionSize = position ? parseFloat(position.size, 10) : 0;
  const positionDirection = side === 'buy' ? 1 : -1;
  const rawOrderValue = getOrderValue(
    selected_product.notional_type,
    selected_product.contract_value,
    orderPrice || markPrice,
    orderSize,
    selected_product.contract_type,
    spotPrice
  );

  let orderValue;

  if (selected_product.notional_type === 'inverse') {
    orderValue = cropAfterDecimals(rawOrderValue, 4);
  } else if (isFutures(contractType)) {
    orderValue = cropAfterDecimals(rawOrderValue, 2);
  } else {
    orderValue = round_by_contract_size(rawOrderValue, selected_product.contract_value);
  }

  const isProductAnOption = isOptions(contractType);

  const isProductAFuture = isFutures(contractType);

  const oldPosition = {
    size: positionSize,
    margin: position ? parseFloat(position.margin, 10) : 0.0,
    entry_price: position ? parseFloat(position.entry_price, 10) : 1,
  };
  const newOrder = {
    size: orderSize * positionDirection,
    side,
    leverage,
    price: Number(orderPrice),
  };

  const { resultingPosition, liquidationPrice } = calculateLiquidationPrice(
    oldPosition,
    newOrder,
    markPrice,
    spotPrice,
    selected_product,
    reduceOnly
  );

  const roundedOrderMargin = cropAfterDecimals(
    convertExponentialToDecimal(orderMargin),
    selected_product.settling_asset.minimum_precision
  );

  const marginValue = (() => {
    switch (contractType) {
      case 'put_options':
      case 'call_options':
      case 'move_options':
      case 'options_combos':
        if (marginType === MARGIN_TYPES.CROSS || isBuyActionState) {
          return (
            <>
              {roundedOrderMargin}{' '}
              <Box as="span" textColor="secondary">
                {selected_product.settling_asset.symbol}
              </Box>
            </>
          );
        }
        return (
          <>
            {roundedOrderMargin}{' '}
            <Box as="span" textColor="secondary">
              {selected_product.settling_asset.symbol} @{' '}
              {cropAfterDecimals(Number(leverage), 2)}x
            </Box>
          </>
        );
      case 'turbo_put_options':
      case 'turbo_call_options':
        return `${roundedOrderMargin} ${selected_product.settling_asset.symbol}`;
      default:
        if (marginType === MARGIN_MODE.CROSS) {
          return (
            <>
              {roundedOrderMargin}{' '}
              <Box as="span" textColor="secondary">
                {selected_product.settling_asset.symbol}
              </Box>
            </>
          );
        }

        return (
          <>
            {roundedOrderMargin}{' '}
            <Box as="span" textColor="secondary">
              {selected_product.settling_asset.symbol} @{' '}
              {cropAfterDecimals(Number(leverage), 2)}x
            </Box>
          </>
        );
    }
  })();

  const orderMetricsLabel = getOrderMetricsLabel({
    contractType: selected_product.contract_type,
  });

  const formattedLiquidationPrice = getFormattedLiquidationPrice({
    isProductAnOption,
    isBuyActionState,
    liquidationPrice,
    productTickSize,
    priceSymbol,
    marginType,
  });

  let estPortfolioMarginValue = estimatedPortfolioMargin;

  if (estPortfolioMarginValue !== null || estPortfolioMarginValue !== undefined) {
    estPortfolioMarginValue =
      isNan(estimatedPortfolioMargin) || Number(estimatedPortfolioMargin) < 0
        ? 0
        : estimatedPortfolioMargin;
  }

  let confirmationTable = [
    {
      label: <LocalizedValue localeKey={orderMetricsLabel} />,
      value: (
        <>
          {orderValue}{' '}
          <Box as="span" textColor="secondary">
            {assetSymbol}
          </Box>
        </>
      ),
      id: 'orderMetrics',
    },
    {
      label: marginLabel,
      value: marginValue,
      id: 'margin',
    },

    // {
    //   label: <LocalizedValue localeKey="availableBalance" />,
    //   value: (
    //     <>
    //       {roundedBalance}{' '}
    //       <Box as="span" textColor="secondary">
    //         {selected_product.settling_asset.symbol}
    //       </Box>
    //     </>
    //   ),
    //   id: 'availableBalance',
    // },
    {
      label: marginLabel,
      value: (
        <>
          {estPortfolioMarginValue}{' '}
          <Box as="span" textColor="secondary">
            {selected_product?.settling_asset?.symbol}
          </Box>
        </>
      ),
      id: 'estimatedMargin',
    },
    {
      label: getLabelByContractType(contractType, i18n.t('trading:limitPrice')),
      value: (
        <>
          {Number(round_by_tick_size(orderPrice, productTickSize))}{' '}
          <Box as="span" textColor="secondary">
            {priceSymbol}
          </Box>
        </>
      ),
      // value: `${orderPrice} ${priceSymbol}`,
      id: 'executionPrice',
    },
    {
      label: getLabelByContractType(contractType, i18n.t('trading:estLiquidationPrice')),
      value: formattedLiquidationPrice,
      className: 'highlight',
      id: 'liquidationPrice',
    },
    {
      label: <LocalizedValue localeKey="posSizeAfterExec" />,
      value: (
        <>
          {Math.round(resultingPosition.netPosition)}{' '}
          <Box as="span" textColor="secondary">
            {pluralize({
              count: Math.round(resultingPosition.netPosition),
              noun: generateAssetUnitLabel('', {
                capitalize: true,
              }),
              exclusions: [1, -1, 0],
              isCountShown: false,
            })}
          </Box>
        </>
      ),
      id: 'positionSize',
    },
  ];
  const marginIndex = confirmationTable.findIndex(item => item.id === 'margin');
  const estPortfolioMarginIndex = confirmationTable.findIndex(
    item => item.id === 'estimatedMargin'
  );
  const index =
    marginType === MARGIN_TYPES.PORTFOLIO ? estPortfolioMarginIndex : marginIndex;
  confirmationTable.splice(
    index + 1,
    0,
    calculateEstimatedTradingFee({
      price: priceToUse(
        selected_product,
        side,
        orderSize,
        markPrice,
        orderBook,
        orderPrice,
        orderType,
        price_band
      ),
      contract_size: selected_product.contract_value,
      size: orderSize,
      settling_asset: selected_product.settling_asset,
      spotPrice,
      maker_commission: selected_product.maker_commission_rate,
      taker_commission: selected_product.taker_commission_rate,
      postOnly,
      contractType,
      estimatedExecPrice: 0,
      orderType,
      notional_type: selected_product.notional_type,
      position: position || 0,
      isConfirmationTable: true,
      premiumCommissionRate: selected_product.product_specs?.premium_commission_rate,
    })
  );

  if (marginType === MARGIN_TYPES.PORTFOLIO) {
    confirmationTable = confirmationTable.filter(
      item => item.id !== 'margin' && item.id !== 'estTradingfee'
    );
  }
  if ([MARGIN_TYPES.PORTFOLIO, MARGIN_TYPES.CROSS].includes(marginType)) {
    confirmationTable = confirmationTable.filter(item => item.id !== 'liquidationPrice');
  }
  if (marginType !== MARGIN_TYPES.PORTFOLIO) {
    confirmationTable = confirmationTable.filter(
      item => item.id !== 'estimatedMargin' && item.id !== 'estimatedPortfolioTradingFee'
    );
  }

  // isBuyActionState has been removed and introducing premium paid

  // for long orders (buy) we don't need to show premium for options
  if (isProductAnOption && !isBuyActionState) {
    confirmationTable.splice(
      3,
      0,
      getEstimatedPremiumTobeReceived({
        price: orderPrice,
        contract_size: selected_product.contract_value,
        size: orderSize,
        settling_asset: selected_product.settling_asset,
        isBuyActionState,
      })
    );
  }

  if (isProductAnOption || isProductAFuture) {
    confirmationTable = confirmationTable.filter(
      ({ id }) => !['executionPrice'].includes(id)
    );
  }

  const priceBandingMsg =
    side === 'buy'
      ? i18n.t('trading:priceBandingMsgBuy')
      : i18n.t('trading:priceBandingMsgSell');

  return {
    confirmationTable,
    priceBandingMsg,
  };
};

export const marketPlaceOrderConfirmationTable = ({
  selected_product,
  position,
  side,
  orderSize,
  orderMargin,
  isBuyActionState,
  leverage,
  estimatedExecPrice,
  marginType,
  estimatedPortfolioMargin,
  reduceOnly,
  orderBook,
  orderType,
}) => {
  const markPrice = markPriceState();
  const spotPrice = spotPriceState();
  const { contract_type: contractType } = selected_product;
  const assetSymbol = (() => {
    switch (contractType) {
      case 'move_options':
      case 'call_options':
      case 'put_options':
      case 'turbo_put_options':
      case 'turbo_call_options':
      case 'options_combos':
        return selected_product?.underlying_asset?.symbol;
      default:
        return selected_product?.settling_asset?.symbol;
    }
  })();
  const priceSymbol =
    contractType === 'interest_rate_swaps' ? '%' : selected_product.quoting_asset.symbol;
  const productTickSize = selected_product.tick_size;
  // const assetId = selected_product.settling_asset.id;
  // const contractVal = parseFloat(selected_product.contract_value, 10);
  const positionSize = position ? parseFloat(position.size, 10) : 0;
  const positionDirection = side === 'buy' ? 1 : -1;

  const oldPosition = {
    size: positionSize,
    margin: position ? parseFloat(position.margin, 10) : 0.0,
    entry_price: position ? parseFloat(position.entry_price, 10) : 1,
  };
  const newOrder = {
    size: orderSize * positionDirection,
    leverage,
    price: parseFloat(estimatedExecPrice, 10),
  };

  const { resultingPosition, liquidationPrice } = calculateLiquidationPrice(
    oldPosition,
    newOrder,
    markPrice,
    spotPrice,
    selected_product,
    reduceOnly
  );

  const rawOrderValue = getOrderValue(
    selected_product.notional_type,
    selected_product.contract_value,
    markPrice,
    orderSize,
    selected_product.contract_type,
    spotPrice
  );

  let orderValue;

  if (selected_product.notional_type === 'inverse') {
    orderValue = cropAfterDecimals(rawOrderValue, 4);
  } else if (isFutures(contractType)) {
    orderValue = cropAfterDecimals(rawOrderValue, 2);
  } else {
    orderValue = round_by_contract_size(rawOrderValue, selected_product.contract_value);
  }

  const isProductAnOption = isOptions(contractType);

  const roundedOrderMargin = cropAfterDecimals(
    convertExponentialToDecimal(orderMargin),
    selected_product?.settling_asset?.minimum_precision
  );

  const marginValue = (() => {
    switch (contractType) {
      case 'put_options':
      case 'call_options':
      case 'move_options':
      case 'options_combos':
        if (marginType === MARGIN_TYPES.CROSS || isBuyActionState) {
          return (
            <>
              {roundedOrderMargin}{' '}
              <Box as="span" textColor="secondary">
                {selected_product.settling_asset.symbol}
              </Box>
            </>
          );
        }
        return (
          <>
            {roundedOrderMargin}{' '}
            <Box as="span" textColor="secondary">
              {selected_product.settling_asset.symbol} @{' '}
              {cropAfterDecimals(Number(leverage), 2)}x
            </Box>
          </>
        );
      case 'turbo_put_options':
      case 'turbo_call_options':
        return (
          <>
            {roundedOrderMargin}{' '}
            <Box as="span" textColor="secondary">
              {selected_product.settling_asset.symbol}
            </Box>
          </>
        );
      default:
        if (marginType === MARGIN_TYPES.CROSS) {
          return (
            <>
              {roundedOrderMargin}{' '}
              <Box as="span" textColor="secondary">
                {selected_product.settling_asset.symbol}
              </Box>
            </>
          );
        }
        return (
          <>
            {roundedOrderMargin}{' '}
            <Box as="span" textColor="secondary">
              {selected_product.settling_asset.symbol} @{' '}
              {cropAfterDecimals(Number(leverage), 2)}x
            </Box>
          </>
        );
    }
  })();

  // const getPrecision = assetPrecision(balances[assetId]?.asset);
  // let balance = balances[assetId]?.available_balance;
  // if (margin_mode === MARGIN_TYPES.CROSS && contractType !== 'spot') {
  //   if (isOptions(contractType) && isBuyActionState) {
  //     balance = multiCollateralData?.available_margin_long_options;
  //   } else {
  //     balance = multiCollateralData?.available_margin_usdt;
  //   }
  // }
  // const roundedBalance = negativeBalanceCheck(balance, getPrecision);

  const orderMetricsLabel = getOrderMetricsLabel({
    contractType: selected_product.contract_type,
  });

  const formattedLiquidationPrice = getFormattedLiquidationPrice({
    isProductAnOption,
    isBuyActionState,
    liquidationPrice,
    productTickSize,
    priceSymbol,
    customFormatting: defaultToHiphen,
    marginType,
  });

  let estPortfolioMarginValue = estimatedPortfolioMargin;

  if (estPortfolioMarginValue !== null || estPortfolioMarginValue !== undefined) {
    estPortfolioMarginValue =
      isNan(estimatedPortfolioMargin) || Number(estimatedPortfolioMargin) < 0
        ? 0
        : estimatedPortfolioMargin;
  }

  let confirmationTable = [
    {
      label: <LocalizedValue localeKey={orderMetricsLabel} />,
      value: (
        <>
          {orderValue}{' '}
          <Box as="span" textColor="secondary">
            {assetSymbol}
          </Box>
        </>
      ),
      id: 'orderMetrics',
    },
    {
      label: marginLabel,
      value: marginValue,
      id: 'margin',
    },

    // {
    //   label: <LocalizedValue localeKey="availableBalance" />,
    //   value: (
    //     <>
    //       {roundedBalance}{' '}
    //       <Box as="span" textColor="secondary">
    //         {selected_product.settling_asset.symbol}
    //       </Box>
    //     </>
    //   ),
    //   id: 'availableBalance',
    // },
    {
      label: marginLabel,
      value: (
        <>
          {defaultToHiphen(Number(estPortfolioMarginValue))}{' '}
          <Box as="span" textColor="secondary">
            {selected_product?.settling_asset?.symbol}
          </Box>
        </>
      ),

      id: 'estimatedMargin',
    },
    {
      label: getLabelByContractType(contractType, i18n.t('trading:estExecPrice')),
      value: (
        <>
          {defaultToHiphen(Number(estimatedExecPrice))}{' '}
          <Box as="span" textColor="secondary">
            {priceSymbol}
          </Box>
        </>
      ),
      id: 'executionPrice',
    },
    {
      label: getLabelByContractType(contractType, i18n.t('trading:estLiquidationPrice')),
      value: formattedLiquidationPrice,
      className: 'highlight',
      id: 'liquidationPrice',
    },
    {
      label: <LocalizedValue localeKey="posSizeAfterExec" />,
      value: (
        <>
          {Math.round(resultingPosition.netPosition)}{' '}
          <Box as="span" textColor="secondary">
            {pluralize({
              count: Math.round(resultingPosition.netPosition),
              noun: generateAssetUnitLabel('', {
                capitalize: true,
              }),
              exclusions: [1, -1, 0],
              isCountShown: false,
            })}
          </Box>
        </>
      ),
      id: 'positionSize',
    },
  ];
  const marginIndex = confirmationTable.findIndex(item => item.id === 'margin');
  const estPortfolioMarginIndex = confirmationTable.findIndex(
    item => item.id === 'estimatedMargin'
  );
  const index =
    marginType === MARGIN_TYPES.PORTFOLIO ? estPortfolioMarginIndex : marginIndex;
  confirmationTable.splice(
    index + 1,
    0,
    calculateEstimatedTradingFee({
      price: priceToUse(
        selected_product,
        side,
        orderSize,
        markPrice,
        orderBook,
        undefined, // since its a market order confirmation popup, order price is not available
        orderType,
        undefined // since its a market order confirmation popup, for market order no need of price band
      ),
      contract_size: selected_product.contract_value,
      size: orderSize,
      settling_asset: selected_product.settling_asset,
      spotPrice,
      maker_commission: selected_product.maker_commission_rate,
      taker_commission: selected_product.taker_commission_rate,
      postOnly: false,
      contractType,
      estimatedExecPrice,
      orderType,
      notional_type: selected_product.notional_type,
      position,
      isConfirmationTable: true,
      premiumCommissionRate: selected_product.product_specs?.premium_commission_rate,
    })
  );

  if (marginType === MARGIN_TYPES.PORTFOLIO) {
    confirmationTable = confirmationTable.filter(item => item.id !== 'margin');
  }
  if ([MARGIN_TYPES.PORTFOLIO, MARGIN_TYPES.CROSS].includes(marginType)) {
    confirmationTable = confirmationTable.filter(item => item.id !== 'liquidationPrice');
  }
  if (marginType !== MARGIN_TYPES.PORTFOLIO) {
    confirmationTable = confirmationTable.filter(item => item.id !== 'estimatedMargin');
  }
  // isBuyActionState has been removed and introducing premium received

  // for long orders (buy) we don't need to show premium for options
  if (isProductAnOption && !isBuyActionState) {
    confirmationTable.splice(
      3,
      0,
      getEstimatedPremiumTobeReceived({
        price: markPrice,
        contract_size: selected_product.contract_value,
        size: orderSize,
        settling_asset: selected_product.settling_asset,
        isBuyActionState,
      })
    );
  }

  const priceBandingMsg =
    side === 'buy'
      ? i18n.t('trading:marketOrderPriceBandingMsgBuy')
      : i18n.t('trading:marketOrderPriceBandingMsgSell');

  return {
    confirmationTable,
    priceBandingMsg,
  };
};

// call this when limit price is not available and have to impactprice from orderbook
export const spotMarketPlaceOrderConfirmationTable = ({
  selected_product,
  side,
  orderSize,
  isQuoteAsset,
  balances,
  isBuyActionState,
  orderbook,
}) => {
  const price = calculateSpotImpactPrice(
    orderbook,
    side,
    orderSize,
    selected_product,
    isQuoteAsset
  );

  const { confirmationTable, priceBandingMsg } = getSpotConfirmationTable({
    selected_product,
    balances,
    price,
    isQuoteAsset,
    isBuyActionState,
    orderSize,
  });
  return {
    confirmationTable,
    priceBandingMsg,
  };
};

// call this when limit price or stop price available is available
export const getSpotConfirmationTable = ({
  selected_product,
  balances,
  price,
  isQuoteAsset,
  isBuyActionState,
  orderSize,
  multiCollateralData,
  margin_mode,
}) => {
  const {
    underlying_asset: { symbol: underlyingSymbol, id: underlyingAssetId },
    quoting_asset: { symbol: quotingSymbol, id: quotingAssetId },
  } = selected_product;

  const balanceAssetId = isBuyActionState ? quotingAssetId : underlyingAssetId;

  const buyingCurrency = isBuyActionState ? underlyingSymbol : quotingSymbol;
  const sellingCurrency = isBuyActionState ? quotingSymbol : underlyingSymbol;

  const buyValue = isBuyActionState
    ? !isQuoteAsset
      ? orderSize
      : spotUnderlyingNotional(price, orderSize) // will get underlying
    : !isQuoteAsset
    ? spotQuoteNotional(price, orderSize)
    : orderSize; // will get quoting

  const sellValue = isBuyActionState
    ? !isQuoteAsset
      ? spotQuoteNotional(price, orderSize)
      : orderSize // will give quoting
    : !isQuoteAsset
    ? orderSize
    : spotUnderlyingNotional(price, orderSize); // will give underlying

  const buyPrice = isBuyActionState
    ? negativeBalanceCheck(buyValue, assetPrecision(selected_product.underlying_asset))
    : Number(round_by_tick_size(buyValue, selected_product.tick_size));
  const sellPrice = isBuyActionState
    ? Number(round_by_tick_size(sellValue, selected_product.tick_size))
    : negativeBalanceCheck(sellValue, assetPrecision(selected_product.underlying_asset));

  let balance = balances[balanceAssetId];
  const balanceSymbol = isBuyActionState ? quotingSymbol : underlyingSymbol;

  if (margin_mode === MARGIN_TYPES.CROSS) {
    if (balances[balanceAssetId]?.updated_at < multiCollateralData.updated_at) {
      const wallet = multiCollateralData?.wallets?.filter(
        obj => obj.asset_id === balanceAssetId
      );
      if (wallet.length > 0) {
        balance = wallet[0].available_balance;
      }
    }
  }

  const confirmationTable = [
    {
      label: `${buyingCurrency} ${i18n.t('trading:youWillGet')}`,
      value: `${buyPrice}`,
    },
    {
      label: `${sellingCurrency} ${i18n.t('trading:youWillGive')}`,
      value: `${sellPrice}`,
    },
    {
      label: <LocalizedValue localeKey="availableBalance" />,
      value: `${
        balance &&
        negativeBalanceCheck(balance.available_balance, assetPrecision(balance.asset))
      } ${balanceSymbol}`,
    },
  ];

  const priceBandingMsg = '';

  return {
    confirmationTable,
    priceBandingMsg,
  };
};

export const getTrackerConfirmationTable = ({
  selected_product,
  price,
  orderSize,
  isMarketOrder,
  isBuyActionState,
}) => {
  const {
    underlying_asset: {
      symbol: underlyingSymbol,
      minimum_precision: underlyingPrecision,
    },
    settling_asset: { symbol: settlingSymbol },
    contract_value: contractValue,
    tick_size: productTickSize,
    contract_type: contractType,
  } = selected_product;

  const qty = Number(orderSize) * Number(contractValue);
  const roundedQty = round(qty, underlyingPrecision);

  const confirmationTable = [
    {
      label: <LocalizedValue localeKey="orderSize" />,
      value: `${roundedQty} ${trackerSymbolText(underlyingSymbol)}`,
    },
    {
      label: isMarketOrder
        ? i18n.t('trading:executionPrice')
        : getLabelByContractType(contractType, i18n.t('trading:limitPrice')),
      value: (
        <>
          {Number(round_by_tick_size(price, productTickSize))}{' '}
          <Box as="span" textColor="secondary">
            {settlingSymbol}
          </Box>
        </>
      ),
      // value: `${orderPrice} ${priceSymbol}`,
      id: 'executionPrice',
    },
  ];

  const priceBandingMsg = (() => {
    if (isMarketOrder) {
      return isBuyActionState
        ? i18n.t('trading:marketOrderPriceBandingMsgBuy')
        : i18n.t('trading:marketOrderPriceBandingMsgSell');
    }
    return isBuyActionState
      ? i18n.t('trading:priceBandingMsgBuy')
      : i18n.t('trading:priceBandingMsgSell');
  })();

  return {
    confirmationTable,
    priceBandingMsg,
  };
};

export const stopOrderConfirmationTable = ({
  selected_product,
  position,
  limitPrice,
  orderSize,
  leverage,
  side,
  reduceOnly,
  // balances,
}) => {
  const spotPrice = spotPriceState();
  const markPrice = markPriceState();

  const price = limitPrice || markPrice;

  const assetSymbol = (() => {
    switch (selected_product.contract_type) {
      case 'move_options':
      case 'call_options':
      case 'put_options':
      case 'turbo_put_options':
      case 'turbo_call_options':
      case 'options_combos':
        return selected_product.underlying_asset.symbol;

      default:
        return selected_product.settling_asset.symbol;
    }
  })();

  // const assetId = selected_product.settling_asset.id;
  // const contractVal = parseFloat(selected_product.contract_value, 10);
  const positionSize = position ? parseFloat(position.size, 10) : 0;
  const positionDirection = side === 'buy' ? 1 : -1;
  const rawOrderValue = getOrderValue(
    selected_product.notional_type,
    selected_product.contract_value,
    price,
    orderSize,
    selected_product.contract_type,
    spotPrice
  );

  let orderValue;

  if (selected_product.notional_type === 'inverse') {
    orderValue = cropAfterDecimals(rawOrderValue, 4);
  } else if (isFutures(selected_product.contract_type)) {
    orderValue = cropAfterDecimals(rawOrderValue, 2);
  } else {
    orderValue = round_by_contract_size(rawOrderValue, selected_product.contract_value);
  }
  const oldPosition = {
    size: positionSize,
    margin: position ? parseFloat(position.margin, 10) : 0.0,
    entry_price: position ? parseFloat(position.entry_price, 10) : 1,
  };
  const newOrder = {
    size: orderSize * positionDirection,
    leverage,
    price: Number(price),
  };

  const { resultingPosition } = calculateLiquidationPrice(
    oldPosition,
    newOrder,
    price,
    spotPrice,
    selected_product,
    reduceOnly
  );

  // const getPrecision = assetPrecision(balances[assetId].asset);
  const orderMetricsLabel = getOrderMetricsLabel({
    contractType: selected_product.contract_type,
  });

  const confirmationTable = [
    {
      label: <LocalizedValue localeKey={orderMetricsLabel} />,
      value: (
        <>
          {orderValue}{' '}
          <Box as="span" textColor="secondary">
            {assetSymbol}
          </Box>
        </>
      ),
      id: 'orderMetrics',
    },
    // {
    //   label: <LocalizedValue localeKey="availableBalance" />,
    //   value: (
    //     <>
    //       {balances[assetId] &&
    //         negativeBalanceCheck(balances[assetId].available_balance, getPrecision)}{' '}
    //       <Box as="span" textColor="secondary">
    //         {selected_product.settling_asset.symbol}
    //       </Box>
    //     </>
    //   ),
    //   id: 'availableBalance',
    // },
    {
      label: <LocalizedValue localeKey="posSizeAfterExec" />,
      value: (
        <>
          {Math.round(resultingPosition.netPosition)}{' '}
          <Box as="span" textColor="secondary">
            {pluralize({
              count: Math.round(resultingPosition.netPosition),
              noun: generateAssetUnitLabel('', {
                capitalize: true,
              }),
              exclusions: [1, -1, 0],
              isCountShown: false,
            })}
          </Box>
        </>
      ),
      id: 'posSizeAfterExec',
    },
  ];

  return { confirmationTable };
};

export const getAssetSymbol = selectedProduct => {
  switch (selectedProduct.contract_type) {
    case 'move_options':
    case 'call_options':
    case 'put_options':
    case 'turbo_put_options':
    case 'turbo_call_options':
    case 'options_combos':
      return selectedProduct.underlying_asset?.symbol;
    default:
      return selectedProduct.settling_asset?.symbol;
  }
};

export const calculateTakeProfitEstPrice = ({ val, side, orderPrice, tick_size }) => {
  if (!val || !orderPrice || isNan(orderPrice) || !(Number(orderPrice) > 0)) {
    return '';
  }
  if (side === 'buy') {
    return round_by_tick_size(orderPrice * (1 + val / 100), tick_size);
  }
  return round_by_tick_size(orderPrice * (1 - val / 100), tick_size);
};

export const calculateStopLossEstPrice = ({ val, side, orderPrice, tick_size }) => {
  if (!val || !orderPrice || isNan(orderPrice) || !(Number(orderPrice) > 0)) {
    return '';
  }
  if (side === 'buy') {
    return round_by_tick_size(orderPrice * (1 - val / 100), tick_size);
  }
  return round_by_tick_size(orderPrice * (1 + val / 100), tick_size);
};

export const calculateTakeProfitEstPercent = ({ val, side, orderPrice }) => {
  if (!val || !orderPrice || isNan(orderPrice) || !(Number(orderPrice) > 0)) {
    return '';
  }
  if (side === 'buy') {
    const estPercent = (((val - orderPrice) / orderPrice) * 100).toFixed(2);
    return estPercent > 0 ? estPercent : '';
  }
  const estPercent = (((orderPrice - val) / orderPrice) * 100).toFixed(2);
  return estPercent > 0 ? estPercent : '';
};

export const calculateStopLossEstPercent = ({ val, side, orderPrice }) => {
  if (!val || !orderPrice || isNan(orderPrice) || !(Number(orderPrice) > 0)) {
    return '';
  }
  if (side === 'buy') {
    const estPercent = (((orderPrice - val) / orderPrice) * 100).toFixed(2);
    return estPercent > 0 ? estPercent : '';
  }
  const estPercent = (((val - orderPrice) / orderPrice) * 100).toFixed(2);
  return estPercent > 0 ? estPercent : '';
};

export const isValidTakeProfitValue = ({ takeProfitEstPrice, orderPrice, side }) => {
  if (!takeProfitEstPrice) {
    return true;
  }

  if (side === 'buy' && Number(takeProfitEstPrice) > Number(orderPrice)) {
    return true;
  }

  if (side === 'sell' && Number(takeProfitEstPrice) < Number(orderPrice)) {
    return true;
  }

  return false;
};

export const isValidStopLossValue = ({ stopLossEstPrice, orderPrice, side }) => {
  if (!stopLossEstPrice) {
    return true;
  }

  if (side === 'buy' && Number(stopLossEstPrice) < Number(orderPrice)) {
    return true;
  }

  if (side === 'sell' && Number(stopLossEstPrice) > Number(orderPrice)) {
    return true;
  }

  return false;
};

export const availableBalanceHookForClassComponent = Component => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  return function WrappedComponent(props) {
    const { selected_product, isBuyActionState } = props;
    const { roundedBalance } = useSelectedProductBalance(
      selected_product,
      isBuyActionState
    );
    return <Component {...props} roundedBalance={roundedBalance} />;
  };
};
